import { useEffect, useRef, useState } from "react";
import { useParams, useLocation, Link, useNavigate } from "react-router-dom";
import {
  serviceCategories,
  testimonials,
  portfolioItems,
  consultationLink,
  landscapeVideos,
  shortsVideos,
} from "../data/mock";
import { Badge } from "../components/ui/badge";
import { Button } from "../components/ui/button";
import {
  Check,
  ArrowRight,
  ArrowUpRight,
  Star,
  Play,
  ChevronRight,
  Megaphone,
  Video,
  Newspaper,
  Globe,
  Zap,
  Share2,
  Target,
  Users,
  MessageCircle,
  MapPin,
  Settings,
  Camera,
  Film,
  Scissors,
  User,
  FileText,
  Mic,
  Tv,
  Palette,
  Code,
  Smartphone,
  ShoppingCart,
  Briefcase,
  Bot,
  Lightbulb,
  TrendingUp,
  Mail,
  Linkedin,
} from "lucide-react";
import { motion } from "framer-motion";

// Category icon mapping
const categoryIconMap = {
  "digital-marketing": Megaphone,
  "content-creation": Video,
  "publicity-pr": Newspaper,
  "web-design-it": Globe,
  "trending": Zap,
};

// Sub-service icon mapping
const subServiceIconMap = {
  "social-media-management": Share2,
  "performance-marketing": Target,
  "influencer-marketing": Users,
  "whatsapp-api-marketing": MessageCircle,
  "google-my-business": MapPin,
  "marketing-automation": Settings,
  "reels-production": Film,
  "photography-videography": Camera,
  "video-editing": Scissors,
  "personal-branding": User,
  "copywriting-content": FileText,
  "personal-branding-pr": User,
  "media-news-coverage": Newspaper,
  "podcast-production": Mic,
  "tv-production": Tv,
  "ui-ux-design": Palette,
  "website-development": Code,
  "app-development": Smartphone,
  "ecommerce-solutions": ShoppingCart,
  "business-automation": Briefcase,
  "ai-tools-automation": Bot,
  "lead-generation": Lightbulb,
  "personal-brand-growth": TrendingUp,
  "email-marketing": Mail,
  "linkedin-outreach": Linkedin,
};

// YouTube embed params to hide branding and end screen
const YOUTUBE_EMBED_PARAMS = "rel=0&modestbranding=1&showinfo=0&iv_load_policy=3&controls=1&fs=1&cc_load_policy=0&autoplay=1";

// Custom video player component with native look
const VideoPlayer = ({ youtubeId, title, isShort = false, className = "" }) => {
  const [playing, setPlaying] = useState(false);
  const thumbUrl = `https://img.youtube.com/vi/${youtubeId}/${isShort ? "0" : "maxresdefault"}.jpg`;

  if (playing) {
    return (
      <div className={`relative overflow-hidden rounded-xl bg-black ${className}`}>
        <iframe
          src={`https://www.youtube.com/embed/${youtubeId}?autoplay=1&${YOUTUBE_EMBED_PARAMS}`}
          title={title}
          frameBorder="0"
          allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
          allowFullScreen
          className="w-full h-full absolute inset-0"
          loading="lazy"
        />
      </div>
    );
  }

  return (
    <div
      className={`relative overflow-hidden rounded-xl cursor-pointer group bg-black ${className}`}
      onClick={() => setPlaying(true)}
    >
      <img
        src={thumbUrl}
        alt={title}
        className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-500 opacity-80 group-hover:opacity-100"
        loading="lazy"
      />
      <div className="absolute inset-0 bg-gradient-to-t from-black/60 via-transparent to-black/20" />
      <div className="absolute inset-0 flex items-center justify-center">
        <motion.div
          whileHover={{ scale: 1.1 }}
          whileTap={{ scale: 0.95 }}
          className="w-12 h-12 rounded-full bg-[#dc2626] flex items-center justify-center shadow-lg shadow-[#dc2626]/30"
        >
          <Play className="w-5 h-5 text-white fill-white ml-0.5" />
        </motion.div>
      </div>
      {title && (
        <div className="absolute bottom-0 left-0 right-0 p-3 bg-gradient-to-t from-black/80 to-transparent">
          <p className="text-white text-xs font-medium line-clamp-2">{title}</p>
        </div>
      )}
    </div>
  );
};

export default function ServicePage() {
  const { categoryId } = useParams();
  const location = useLocation();
  const navigate = useNavigate();

  const category = serviceCategories.find((c) => c.id === categoryId);

  // Scroll to hash on load
  useEffect(() => {
    if (location.hash) {
      const id = location.hash.replace("#", "");
      setTimeout(() => {
        const el = document.getElementById(id);
        if (el) {
          el.scrollIntoView({ behavior: "smooth", block: "start" });
        }
      }, 300);
    } else {
      window.scrollTo(0, 0);
    }
  }, [location]);

  if (!category) {
    return (
      <div className="min-h-screen flex items-center justify-center pt-20">
        <div className="text-center">
          <h1 className="text-3xl font-bold text-white mb-4">Service Not Found</h1>
          <Link to="/" className="text-[#dc2626] hover:underline">
            Go back home
          </Link>
        </div>
      </div>
    );
  }

  const relatedTestimonials = testimonials.filter(
    (t) =>
      t.service.toLowerCase().includes(category.title.toLowerCase().split(" ")[0]) ||
      category.subServices.some((s) =>
        t.service.toLowerCase().includes(s.title.toLowerCase().split(" ")[0])
      )
  );
  const displayTestimonials = relatedTestimonials.length > 0 ? relatedTestimonials : testimonials.slice(0, 3);
  const relatedPortfolio = portfolioItems.filter((p) => p.category === categoryId);

  const CategoryIcon = categoryIconMap[category.id] || Zap;

  return (
    <div>
      {/* ===== HERO ===== */}
      <section className="relative min-h-[70vh] flex items-end overflow-hidden">
        <div className="absolute inset-0">
          {/* Use lucide icon as background */}
          <div className="w-full h-full flex items-center justify-center bg-gradient-to-br from-[#0a0a0a] via-[#111] to-[#0a0a0a]">
            <CategoryIcon className="w-[400px] h-[400px] opacity-[0.04]" strokeWidth={0.5} style={{ color: category.color }} />
          </div>
          <div className="absolute inset-0 bg-gradient-to-t from-[#0a0a0a] via-[#0a0a0a]/80 to-[#0a0a0a]/40" />
          <div className="absolute inset-0 bg-gradient-to-r from-[#0a0a0a]/60 to-transparent" />
          
          {/* Wave animation at bottom */}
          <svg
            className="absolute bottom-0 left-0 w-full"
            viewBox="0 0 1440 320"
            preserveAspectRatio="none"
            style={{ height: "30%", opacity: 0.03 }}
          >
            <motion.path
              fill={category.color}
              animate={{
                d: [
                  "M0,192L48,197.3C96,203,192,213,288,229.3C384,245,480,267,576,250.7C672,235,768,181,864,181.3C960,181,1056,235,1152,234.7C1248,235,1344,181,1392,154.7L1440,128L1440,320L1392,320C1344,320,1248,320,1152,320C1056,320,960,320,864,320C768,320,672,320,576,320C480,320,384,320,288,320C192,320,96,320,48,320L0,320Z",
                  "M0,128L48,154.7C96,181,192,235,288,234.7C384,235,480,181,576,181.3C672,181,768,235,864,250.7C960,267,1056,245,1152,229.3C1248,213,1344,203,1392,197.3L1440,192L1440,320L1392,320C1344,320,1248,320,1152,320C1056,320,960,320,864,320C768,320,672,320,576,320C480,320,384,320,288,320C192,320,96,320,48,320L0,320Z",
                  "M0,192L48,197.3C96,203,192,213,288,229.3C384,245,480,267,576,250.7C672,235,768,181,864,181.3C960,181,1056,235,1152,234.7C1248,235,1344,181,1392,154.7L1440,128L1440,320L1392,320C1344,320,1248,320,1152,320C1056,320,960,320,864,320C768,320,672,320,576,320C480,320,384,320,288,320C192,320,96,320,48,320L0,320Z",
                ],
              }}
              transition={{ duration: 8, repeat: Infinity, ease: "easeInOut" }}
            />
          </svg>
        </div>

        <div className="relative max-w-7xl mx-auto px-6 lg:px-8 pb-16 pt-32 w-full">
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6 }}
          >
            <div className="flex items-center gap-2 text-[#a1a1aa] text-sm mb-4">
              <Link to="/" className="hover:text-white transition-colors">Home</Link>
              <ChevronRight className="w-3.5 h-3.5" />
              <span className="text-white">{category.title}</span>
            </div>
            
            {/* Service icon using lucide */}
            <div className="w-16 h-16 rounded-xl flex items-center justify-center mb-6" style={{ backgroundColor: `${category.color}20` }}>
              <CategoryIcon className="w-8 h-8" style={{ color: category.color }} />
            </div>
            
            <h1
              className="text-4xl sm:text-5xl lg:text-6xl font-bold text-white leading-tight tracking-tight mb-4"
              style={{ fontFamily: "'Space Grotesk', sans-serif" }}
            >
              {category.title}
            </h1>
            <p className="text-lg text-[#a1a1aa] max-w-2xl mb-8">
              {category.description}
            </p>
            <div className="flex flex-wrap gap-3">
              <Button
                onClick={() => navigate("/gmf")}
                className="bg-[#dc2626] hover:bg-[#b91c1c] text-white px-8 py-5 rounded-xl text-sm font-semibold transition-colors duration-300 flex items-center gap-2 shadow-lg shadow-[#dc2626]/20"
              >
                Get Started
                <ArrowRight className="w-4 h-4" />
              </Button>
              <Button
                variant="outline"
                className="border-white/[0.12] bg-white/[0.03] hover:bg-white/[0.06] text-white px-8 py-5 rounded-xl text-sm font-medium transition-colors duration-300 backdrop-blur-sm"
                onClick={() => {
                  const el = document.getElementById(category.subServices[0]?.id);
                  if (el) el.scrollIntoView({ behavior: "smooth" });
                }}
              >
                Explore Services
              </Button>
            </div>
          </motion.div>
        </div>
      </section>

      {/* ===== QUICK NAV BAR ===== */}
      <section className="sticky top-20 z-30 bg-[#0a0a0a]/95 backdrop-blur-xl border-b border-white/[0.06]">
        <div className="max-w-7xl mx-auto px-6 lg:px-8">
          <div className="flex gap-1 overflow-x-auto scrollbar-hide py-3" style={{ scrollbarWidth: "none" }}>
            {category.subServices.map((sub) => (
              <button
                key={sub.id}
                onClick={() => {
                  const el = document.getElementById(sub.id);
                  if (el) el.scrollIntoView({ behavior: "smooth", block: "start" });
                }}
                className="shrink-0 px-4 py-2 text-xs font-medium text-[#71717a] hover:text-white hover:bg-white/[0.06] rounded-lg transition-all duration-200"
              >
                {sub.title}
              </button>
            ))}
          </div>
        </div>
      </section>

      {/* ===== SUB-SERVICE SECTIONS ===== */}
      {category.subServices.map((sub, index) => {
        const SubIcon = subServiceIconMap[sub.id] || CategoryIcon;
        return (
        <section
          key={sub.id}
          id={sub.id}
          className="relative py-20 lg:py-28 scroll-mt-32 overflow-hidden"
        >
          {/* Subtle wave background for every other section */}
          {index % 2 === 0 && (
            <div className="absolute inset-0 opacity-[0.015]">
              <svg
                className="absolute bottom-0 left-0 w-full"
                viewBox="0 0 1440 320"
                preserveAspectRatio="none"
                style={{ height: "50%" }}
              >
                <path
                  fill={category.color}
                  d="M0,192L48,197.3C96,203,192,213,288,229.3C384,245,480,267,576,250.7C672,235,768,181,864,181.3C960,181,1056,235,1152,234.7C1248,235,1344,181,1392,154.7L1440,128L1440,320L1392,320C1344,320,1248,320,1152,320C1056,320,960,320,864,320C768,320,672,320,576,320C480,320,384,320,288,320C192,320,96,320,48,320L0,320Z"
                />
              </svg>
            </div>
          )}
          
          <div className="relative max-w-7xl mx-auto px-6 lg:px-8">
            <motion.div
              initial={{ opacity: 0, y: 40 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true, margin: "-100px" }}
              transition={{ duration: 0.6 }}
              className={`grid grid-cols-1 lg:grid-cols-2 gap-12 lg:gap-16 items-center ${
                index % 2 === 1 ? "lg:direction-rtl" : ""
              }`}
            >
              {/* Content */}
              <div className={index % 2 === 1 ? "lg:order-2" : ""}>
                <Badge
                  variant="outline"
                  className="border-[#dc2626]/30 bg-[#dc2626]/[0.08] text-[#ef4444] px-3 py-1 text-xs rounded-full mb-5"
                >
                  {category.title}
                </Badge>
                <h2
                  className="text-3xl lg:text-4xl font-bold text-white mb-4 tracking-tight"
                  style={{ fontFamily: "'Space Grotesk', sans-serif" }}
                >
                  {sub.title}
                </h2>
                <p className="text-[#71717a] text-base leading-relaxed mb-8">
                  {sub.description}
                </p>

                <div className="space-y-3 mb-8">
                  {sub.features.map((feature, fIdx) => (
                    <motion.div
                      key={fIdx}
                      initial={{ opacity: 0, x: -15 }}
                      whileInView={{ opacity: 1, x: 0 }}
                      viewport={{ once: true }}
                      transition={{ delay: fIdx * 0.06 }}
                      className="flex items-start gap-3"
                    >
                      <div className="w-5 h-5 rounded-full bg-[#dc2626]/10 flex items-center justify-center mt-0.5 shrink-0">
                        <Check className="w-3 h-3 text-[#dc2626]" />
                      </div>
                      <span className="text-[#a1a1aa] text-sm">{feature}</span>
                    </motion.div>
                  ))}
                </div>

                <Button
                  onClick={() => navigate("/gmf")}
                  className="bg-[#dc2626] hover:bg-[#b91c1c] text-white px-6 py-5 rounded-xl text-sm font-semibold transition-colors duration-300 flex items-center gap-2"
                >
                  Consult Now
                  <ArrowUpRight className="w-4 h-4" />
                </Button>
              </div>

              {/* Icon-based minimalist visual instead of stock image */}
              <div className={`relative ${index % 2 === 1 ? "lg:order-1" : ""}`}>
                <div className="rounded-2xl overflow-hidden border border-white/[0.06] bg-gradient-to-br from-white/[0.02] to-transparent p-12 lg:p-16 flex items-center justify-center min-h-[350px] lg:min-h-[420px] group">
                  <motion.div
                    whileHover={{ scale: 1.05 }}
                    transition={{ duration: 0.5 }}
                    className="relative"
                  >
                    {/* Large background icon */}
                    <SubIcon 
                      className="w-40 h-40 lg:w-52 lg:h-52 opacity-10 group-hover:opacity-15 transition-opacity duration-500" 
                      strokeWidth={0.5}
                      style={{ color: category.color }} 
                    />
                    {/* Centered smaller icon */}
                    <div 
                      className="absolute top-1/2 left-1/2 transform -translate-x-1/2 -translate-y-1/2 w-24 h-24 rounded-2xl flex items-center justify-center"
                      style={{ backgroundColor: `${category.color}15` }}
                    >
                      <SubIcon className="w-12 h-12" style={{ color: category.color }} />
                    </div>
                  </motion.div>
                </div>
                <div
                  className="absolute -bottom-4 -right-4 w-40 h-40 rounded-full blur-[80px] pointer-events-none opacity-20"
                  style={{ backgroundColor: category.color }}
                />
              </div>
            </motion.div>
          </div>

          {/* Divider */}
          {index < category.subServices.length - 1 && (
            <div className="max-w-7xl mx-auto px-6 lg:px-8 mt-20">
              <div className="h-px bg-gradient-to-r from-transparent via-white/[0.06] to-transparent" />
            </div>
          )}
        </section>
      )})}


      {/* ===== CLIENT VIDEO FEEDBACK ===== */}
      <section className="py-20 lg:py-28">
        <div className="max-w-7xl mx-auto px-6 lg:px-8">
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.6 }}
            className="text-center mb-14"
          >
            <Badge
              variant="outline"
              className="border-[#dc2626]/30 bg-[#dc2626]/[0.08] text-[#ef4444] px-4 py-1.5 text-sm rounded-full mb-6"
            >
              Client Feedback
            </Badge>
            <h2
              className="text-3xl lg:text-4xl font-bold text-white mb-3 tracking-tight"
              style={{ fontFamily: "'Space Grotesk', sans-serif" }}
            >
              See Our <span className="text-[#dc2626]">Results</span>
            </h2>
            <p className="text-[#71717a] text-base max-w-lg mx-auto">
              Watch real client feedback and see how Cube delivers {category.title.toLowerCase()} results.
            </p>
          </motion.div>

          {/* Get only feedback type videos */}
          {(() => {
            const feedbackVideos = landscapeVideos.filter(v => v.type === "feedback");
            const featuredVideo = feedbackVideos[0];
            const otherVideos = feedbackVideos.slice(1);
            
            return (
              <>
                {/* Featured landscape video */}
                {featuredVideo && (
                  <div className="mb-10">
                    <VideoPlayer 
                      youtubeId={featuredVideo.youtubeId} 
                      title={featuredVideo.title} 
                      className="aspect-video max-w-4xl mx-auto border border-white/[0.06]" 
                    />
                  </div>
                )}

                {/* Client feedback videos grid - show all */}
                {otherVideos.length > 0 && (
                  <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4 mb-10">
                    {otherVideos.map((video) => (
                      <VideoPlayer 
                        key={video.id} 
                        youtubeId={video.youtubeId} 
                        title={video.title} 
                        className="aspect-video border border-white/[0.06]" 
                      />
                    ))}
                  </div>
                )}
              </>
            );
          })()}

          {/* Quick shorts - show more */}
          <h3 className="text-xl font-semibold text-white mb-6 text-center" style={{ fontFamily: "'Space Grotesk', sans-serif" }}>
            Quick <span className="text-[#dc2626]">Client Reviews</span>
          </h3>
          <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-6 gap-4">
            {shortsVideos.slice(0, 12).map((video) => (
              <div key={video.id}>
                <VideoPlayer 
                  youtubeId={video.youtubeId} 
                  title={video.title} 
                  isShort 
                  className="aspect-[9/16] border border-white/[0.06]" 
                />
                <div className="mt-2">
                  <p className="text-white text-xs font-medium line-clamp-1">{video.title}</p>
                  <p className="text-[#52525b] text-[10px]">{video.industry}</p>
                </div>
              </div>
            ))}
          </div>

          <div className="text-center mt-10">
            <Button
              onClick={() => navigate("/consultation")}
              variant="outline"
              className="border-[#dc2626]/30 text-[#dc2626] hover:bg-[#dc2626] hover:text-white px-6 py-3 rounded-xl text-sm transition-all duration-300"
            >
              Get Similar Results for Your Brand
            </Button>
          </div>
        </div>
      </section>

      {/* ===== CLIENT FEEDBACK ===== */}
      <section className="py-20 lg:py-28">
        <div className="max-w-7xl mx-auto px-6 lg:px-8">
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.6 }}
            className="mb-14"
          >
            <h2
              className="text-3xl lg:text-4xl font-bold text-white mb-3 tracking-tight"
              style={{ fontFamily: "'Space Grotesk', sans-serif" }}
            >
              Client <span className="text-[#dc2626]">Feedback</span>
            </h2>
            <p className="text-[#71717a] text-base max-w-lg">
              Hear directly from brands who've worked with us on {category.title.toLowerCase()}.
            </p>
          </motion.div>

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
            {displayTestimonials.map((t, i) => (
              <motion.div
                key={t.id}
                initial={{ opacity: 0, y: 30 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ delay: i * 0.1 }}
                className="p-6 rounded-2xl bg-white/[0.03] border border-white/[0.08] hover:border-[#dc2626]/20 transition-all duration-500"
              >
                <div className="flex items-center gap-1 mb-4">
                  {[...Array(t.rating)].map((_, j) => (
                    <Star key={j} className="w-4 h-4 fill-[#dc2626] text-[#dc2626]" />
                  ))}
                </div>
                <p className="text-[#a1a1aa] text-sm leading-relaxed mb-5">
                  &ldquo;{t.review}&rdquo;
                </p>
                <div className="flex items-center justify-between">
                  <div className="flex items-center gap-3">
                    <div className="w-9 h-9 rounded-full bg-[#dc2626]/20 flex items-center justify-center text-[#dc2626] font-bold text-xs">
                      {t.initial}
                    </div>
                    <div>
                      <p className="text-white text-sm font-medium">{t.name}</p>
                      <p className="text-[#52525b] text-xs">{t.service}</p>
                    </div>
                  </div>
                </div>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* ===== BOTTOM CTA ===== */}
      <section className="py-20">
        <div className="max-w-7xl mx-auto px-6 lg:px-8">
          <div className="relative p-12 lg:p-16 rounded-3xl bg-gradient-to-br from-[#dc2626]/10 to-transparent border border-[#dc2626]/20 overflow-hidden">
            <div className="absolute top-0 right-0 w-80 h-80 bg-[#dc2626]/10 rounded-full blur-[100px] pointer-events-none" />
            <div className="relative flex flex-col lg:flex-row items-center justify-between gap-8">
              <div className="text-center lg:text-left">
                <h2
                  className="text-2xl lg:text-4xl font-bold text-white mb-3"
                  style={{ fontFamily: "'Space Grotesk', sans-serif" }}
                >
                  Let's Build Something
                  <span className="text-[#dc2626]"> Amazing Together</span>
                </h2>
                <p className="text-[#a1a1aa] text-base max-w-lg">
                  Book a free consultation with our senior experts and get a customized {category.title.toLowerCase()} strategy for your brand.
                </p>
              </div>
              <Button
                onClick={() => navigate("/gmf")}
                className="bg-[#dc2626] hover:bg-[#b91c1c] text-white px-10 py-6 rounded-xl text-base font-semibold transition-colors duration-300 flex items-center gap-2 shadow-lg shadow-[#dc2626]/20 shrink-0"
              >
                Book Consultation
                <ArrowRight className="w-5 h-5" />
              </Button>
            </div>
          </div>
        </div>
      </section>
    </div>
  );
}
